import { Schema } from "effect";
import type { DomainEvent } from "./DomainEvent";

/**
 * Domain event published when an invoice is successfully created.
 * This event triggers downstream processes like PDF storage, notifications, etc.
 * 
 * Business rules captured:
 * - Invoice has been validated and persisted
 * - PDF has been generated and is ready for storage
 * - All business invariants have been satisfied
 * - Invoice is now immutable
 */
export interface InvoiceCreated extends DomainEvent {
  readonly type: "InvoiceCreated";
  readonly invoiceId: string;
  readonly invoiceNumber: string;
  readonly orderId: string;
  readonly customerId: string;
  readonly createdBy: string;
  readonly grandTotal: {
    readonly amount: number;
    readonly currency: string;
  };
  readonly pdfBytes: Uint8Array;
  readonly legalInfo: {
    readonly complianceStandard: "PDF/A-3";
    readonly germanTaxCompliant: boolean;
    readonly zugferdReady: boolean;
  };
}

/**
 * Schema for InvoiceCreated domain event with validation
 */
export const InvoiceCreatedSchema = Schema.Struct({
  // Base domain event fields
  type: Schema.Literal("InvoiceCreated"),
  eventId: Schema.String.pipe(
    Schema.nonEmptyString(),
    Schema.pattern(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      {
        message: () => "Event ID must be a valid UUID",
      }
    )
  ),
  occurredOn: Schema.DateFromSelf,
  version: Schema.Number.pipe(
    Schema.int(),
    Schema.greaterThanOrEqualTo(1, {
      message: () => "Event version must be a positive integer",
    })
  ),
  aggregateType: Schema.String.pipe(Schema.nonEmptyString()),
  aggregateId: Schema.String.pipe(Schema.nonEmptyString()),
  correlationId: Schema.optional(Schema.String.pipe(Schema.nonEmptyString())),
  causationId: Schema.optional(Schema.String.pipe(Schema.nonEmptyString())),
  
  // InvoiceCreated specific fields
  invoiceId: Schema.String.pipe(
    Schema.nonEmptyString(),
    Schema.pattern(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      {
        message: () => "Invoice ID must be a valid UUID",
      }
    )
  ),
  invoiceNumber: Schema.String.pipe(
    Schema.nonEmptyString(),
    Schema.pattern(/^(INV-\d+|\d+)$/, {
      message: () => "Invoice number must follow format INV-1001 or 1221",
    })
  ),
  orderId: Schema.String.pipe(Schema.nonEmptyString()),
  customerId: Schema.String.pipe(Schema.nonEmptyString()),
  createdBy: Schema.String.pipe(Schema.nonEmptyString()),
  grandTotal: Schema.Struct({
    amount: Schema.Number.pipe(
      Schema.greaterThanOrEqualTo(0, {
        message: () => "Grand total amount must be non-negative",
      })
    ),
    currency: Schema.String.pipe(
      Schema.nonEmptyString(),
      Schema.pattern(/^[A-Z]{3}$/, {
        message: () => "Currency must be a 3-letter ISO code",
      })
    ),
  }),
  pdfBytes: Schema.Uint8ArrayFromSelf,
  legalInfo: Schema.Struct({
    complianceStandard: Schema.Literal("PDF/A-3"),
    germanTaxCompliant: Schema.Boolean,
    zugferdReady: Schema.Boolean,
  }),
});

/**
 * Type alias for the InvoiceCreated event
 */
export type InvoiceCreatedType = Schema.Schema.Type<typeof InvoiceCreatedSchema>;
